import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  FileText, Clock, CheckCircle2, AlertCircle,
  CalendarIcon, Filter, RefreshCw, Loader2, Timer, TrendingUp, AlertTriangle
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';

const ESTADO_COLORS = {
  nuevo: '#3b82f6',
  en_proceso: '#f59e0b',
  terminado: '#10b981',
};

const RESOLUCION_COLORS = {
  sin_resolucion: '#94a3b8',
  borrador: '#f59e0b',
  pendiente_aprobacion: '#8b5cf6',
  aprobada: '#10b981',
};

const TIPO_COLORS = {
  RECLAMO: '#3b82f6',
  QUEJA: '#ef4444',
};

const RESOLUCION_MES_COLORS = {
  aprobadas: '#10b981',
  rechazadas: '#f59e0b',
  pendientes: '#8b5cf6',
};

const MONTH_LABELS = {
  '01': 'Ene', '02': 'Feb', '03': 'Mar', '04': 'Abr',
  '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Ago',
  '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dic',
};

const formatMes = (mes) => {
  if (!mes) return '';
  const [year, month] = mes.split('-');
  return `${MONTH_LABELS[month] || month} ${year}`;
};

const RESOLUCION_LABELS = {
  sin_resolucion: 'Sin Resolucion',
  borrador: 'En Proceso',
  pendiente_aprobacion: 'Pend. Aprobacion',
  aprobada: 'Aprobada',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium text-slate-700 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }} className="font-semibold">
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  );
};

const DashboardPage = () => {
  const { api, empresa, hasRole, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    nuevo: 0, en_proceso: 0, terminado: 0, total: 0,
    reclamos_por_mes: [], por_estado: {}, por_tipo: {},
    por_estado_resolucion: {}, por_proyecto: [],
    resoluciones_por_mes: [], tiempo_promedio_resolucion_dias: 0,
  });
  const [proyectos, setProyectos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [pipelineLoading, setPipelineLoading] = useState(true);
  const canFilterByResponsable = hasRole('ADMINISTRADOR', 'SUPERADMIN', 'SUPERVISOR', 'LEGAL');

  const [filters, setFilters] = useState({
    proyecto_id: '',
    fecha_desde: new Date(new Date().getFullYear(), 0, 1),
    fecha_hasta: new Date(),
    responsable_ids: [],
  });

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.proyecto_id && filters.proyecto_id !== '__all__') {
        params.append('proyecto_id', filters.proyecto_id);
      }
      if (filters.fecha_desde) params.append('fecha_desde', format(filters.fecha_desde, 'yyyy-MM-dd'));
      if (filters.fecha_hasta) params.append('fecha_hasta', format(filters.fecha_hasta, 'yyyy-MM-dd'));
      if (filters.responsable_ids && filters.responsable_ids.length > 0) {
        filters.responsable_ids.forEach(id => params.append('responsable_id', id));
      }
      const response = await api.get(`/dashboard/estadisticas?${params}`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  }, [api, filters]);

  const fetchProyectos = async () => {
    try {
      const response = await api.get('/proyectos');
      setProyectos(response.data);
    } catch (err) {
      console.error('Error fetching proyectos:', err);
    }
  };

  const fetchUsuarios = async () => {
    try {
      const response = await api.get('/usuarios');
      setUsuarios(response.data.filter(u => u.rol === 'USUARIO' || u.rol === 'SUPERVISOR'));
    } catch (err) {
      console.error('Error fetching usuarios:', err);
    }
  };

  const fetchPipeline = useCallback(async () => {
    setPipelineLoading(true);
    try {
      const response = await api.get('/dashboard/pipeline');
      setPipeline(response.data);
    } catch (err) {
      console.error('Error fetching pipeline:', err);
    } finally {
      setPipelineLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchProyectos(); fetchPipeline(); if (canFilterByResponsable) fetchUsuarios(); }, []);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const navToReclamos = (params) => {
    const qs = new URLSearchParams(params).toString();
    navigate(`/admin/reclamos?${qs}`);
  };

  const clearFilters = () => {
    setFilters({
      proyecto_id: '',
      fecha_desde: new Date(new Date().getFullYear(), 0, 1),
      fecha_hasta: new Date(),
      responsable_ids: [],
    });
  };

  // --- Chart data transforms ---
  const areaData = (stats.reclamos_por_mes || []).map(d => ({
    ...d, label: formatMes(d.mes),
  }));

  const estadoDonutData = Object.entries(stats.por_estado || {}).map(([key, value]) => ({
    name: key === 'nuevo' ? 'Nuevo' : key === 'en_proceso' ? 'En Proceso' : 'Terminado',
    value, key,
  })).filter(d => d.value > 0);

  const resolucionDonutData = Object.entries(stats.por_estado_resolucion || {}).map(([key, value]) => ({
    name: RESOLUCION_LABELS[key] || key, value, key,
  })).filter(d => d.value > 0);

  const tipoData = Object.entries(stats.por_tipo || {}).map(([key, value]) => ({
    name: key === 'RECLAMO' ? 'Reclamo' : key === 'QUEJA' ? 'Queja' : key,
    value, key,
  }));

  const proyectoData = (stats.por_proyecto || []).map(d => ({
    ...d, nombre: d.nombre?.length > 20 ? d.nombre.substring(0, 20) + '...' : d.nombre,
    fullName: d.nombre,
  }));

  const resolucionesMesData = (stats.resoluciones_por_mes || []).map(d => ({
    ...d, label: formatMes(d.mes),
  }));

  const handleAreaClick = (data) => {
    if (!data?.activePayload?.[0]?.payload?.mes) return;
    const mes = data.activePayload[0].payload.mes;
    const [y, m] = mes.split('-');
    const lastDay = new Date(Number(y), Number(m), 0).getDate();
    navToReclamos({ fecha_desde: `${mes}-01`, fecha_hasta: `${mes}-${lastDay}` });
  };

  const estadoKeyMap = { nuevo: 'NUEVO', en_proceso: 'EN_PROCESO', terminado: 'TERMINADO' };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">
            Vista general de reclamos {empresa ? `de ${empresa.nombre}` : ''}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchStats} disabled={loading} data-testid="refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Filter className="w-4 h-4" /><span>Filtros:</span>
            </div>
            <Select value={filters.proyecto_id} onValueChange={(v) => setFilters({ ...filters, proyecto_id: v })}>
              <SelectTrigger className="w-[200px]" data-testid="proyecto-filter">
                <SelectValue placeholder="Todos los proyectos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos los proyectos</SelectItem>
                {proyectos.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
            {canFilterByResponsable && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[220px] justify-start text-left font-normal" data-testid="responsable-filter">
                    <Filter className="w-4 h-4 mr-2 flex-shrink-0" />
                    {filters.responsable_ids.length === 0
                      ? 'Todos los responsables'
                      : filters.responsable_ids.length === 1
                        ? usuarios.find(u => u.id === filters.responsable_ids[0])?.nombre || '1 seleccionado'
                        : `${filters.responsable_ids.length} responsables`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[240px] p-2" align="start">
                  <div className="space-y-1 max-h-[220px] overflow-y-auto">
                    {usuarios.map((u) => {
                      const checked = filters.responsable_ids.includes(u.id);
                      return (
                        <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setFilters(prev => ({
                                ...prev,
                                responsable_ids: checked
                                  ? prev.responsable_ids.filter(id => id !== u.id)
                                  : [...prev.responsable_ids, u.id],
                              }));
                            }}
                            className="rounded border-slate-300"
                          />
                          {u.nombre}
                        </label>
                      );
                    })}
                  </div>
                  {filters.responsable_ids.length > 0 && (
                    <Button variant="ghost" size="sm" className="w-full mt-1 text-xs" onClick={() => setFilters(prev => ({ ...prev, responsable_ids: [] }))}>
                      Limpiar selección
                    </Button>
                  )}
                </PopoverContent>
              </Popover>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[150px] justify-start" data-testid="fecha-desde-btn">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {filters.fecha_desde ? format(filters.fecha_desde, 'dd/MM/yyyy') : 'Desde'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={filters.fecha_desde} onSelect={(d) => setFilters({ ...filters, fecha_desde: d })} locale={es} />
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[150px] justify-start" data-testid="fecha-hasta-btn">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {filters.fecha_hasta ? format(filters.fecha_hasta, 'dd/MM/yyyy') : 'Hasta'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={filters.fecha_hasta} onSelect={(d) => setFilters({ ...filters, fecha_hasta: d })} locale={es} />
              </PopoverContent>
            </Popover>
            {(filters.proyecto_id || filters.responsable_ids.length > 0) && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>Limpiar filtros</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard icon={<FileText className="w-6 h-6 text-slate-600" />} label="Total Reclamos" value={stats.total} color="text-slate-900" bgIcon="bg-slate-100" borderColor="border-l-slate-800" loading={loading} onClick={() => navigate('/admin/reclamos')} testId="card-total" />
        <KPICard icon={<AlertCircle className="w-6 h-6 text-blue-600" />} label="Nuevos" value={stats.nuevo} color="text-blue-600" bgIcon="bg-blue-100" borderColor="border-l-blue-500" loading={loading} onClick={() => navToReclamos({ estado: 'NUEVO' })} testId="card-nuevos" />
        <KPICard icon={<Clock className="w-6 h-6 text-amber-600" />} label="En Proceso" value={stats.en_proceso} color="text-amber-600" bgIcon="bg-amber-100" borderColor="border-l-amber-500" loading={loading} onClick={() => navToReclamos({ estado: 'EN_PROCESO' })} testId="card-en-proceso" />
        <KPICard icon={<CheckCircle2 className="w-6 h-6 text-emerald-600" />} label="Terminados" value={stats.terminado} color="text-emerald-600" bgIcon="bg-emerald-100" borderColor="border-l-emerald-500" loading={loading} onClick={() => navToReclamos({ estado: 'TERMINADO' })} testId="card-terminados" />
        <KPICard icon={<Timer className="w-6 h-6 text-violet-600" />} label="Promedio Resolucion" value={`${stats.tiempo_promedio_resolucion_dias || 0}d`} color="text-violet-600" bgIcon="bg-violet-100" borderColor="border-l-violet-500" loading={loading} testId="card-promedio" />
      </div>

      {/* Pipeline de Reclamos - Kanban */}
      <Card className="border-slate-200" data-testid="pipeline-kanban">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-slate-600" />
            Pipeline de Reclamos abiertos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pipelineLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : pipeline ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { key: 'nuevos', title: 'Nuevos', bg: '#dcfce7', border: '#86efac', icon: <AlertCircle className="w-4 h-4" />, aging: 'Desde creación', filter: 'estado=NUEVO' },
                { key: 'en_proceso_sin_res', title: 'En Proceso (sin resolución)', bg: '#a7f3d0', border: '#6ee7b7', icon: <Clock className="w-4 h-4" />, aging: 'Desde asignación', filter: 'estado=EN_PROCESO&estado_resolucion=sin_resolucion' },
                { key: 'en_proceso_con_res', title: 'En Proceso (con resolución)', bg: '#e2c896', border: '#d4a857', icon: <FileText className="w-4 h-4" />, aging: 'Desde resolución', filter: 'estado=EN_PROCESO&estado_resolucion=borrador' },
                { key: 'en_proceso_pend_aprob', title: 'En Proceso (pend. aprobación)', bg: '#f5e6c8', border: '#e8cc8a', icon: <Timer className="w-4 h-4" />, aging: 'Desde envío aprob.', filter: 'estado=EN_PROCESO&estado_resolucion=pendiente_aprobacion' },
                { key: 'en_proceso_aprobada', title: 'En Proceso (res. aprobada)', bg: '#e2e8f0', border: '#cbd5e1', icon: <CheckCircle2 className="w-4 h-4" />, aging: 'Desde aprobación', filter: 'estado=EN_PROCESO&estado_resolucion=aprobada' },
                { key: 'vencidos', title: 'Reclamos vencidos', bg: '#fecaca', border: '#fca5a5', icon: <AlertTriangle className="w-4 h-4" />, aging: 'Desde creación', filter: 'estado=VENCIDO' },
              ].map(({ key, title, bg, border, icon, aging, filter }) => {
                const col = pipeline.columnas[key] || {};
                return (
                  <div
                    key={key}
                    className="rounded-lg border overflow-hidden flex flex-col cursor-pointer transition-shadow hover:shadow-md hover:ring-1 hover:ring-slate-300"
                    style={{ borderColor: border }}
                    onClick={() => col.count > 0 && navigate(`/admin/reclamos?${filter}`)}
                    data-testid={`pipeline-col-${key}`}
                  >
                    <div className="px-3 py-2 text-xs font-semibold text-slate-800 flex items-center gap-1.5" style={{ background: bg }}>
                      {icon} <span className="truncate">{title}</span>
                    </div>
                    <div className="flex-1 px-3 py-3 bg-white space-y-2.5">
                      <div className="text-center">
                        <span className="text-2xl font-bold text-slate-800">{col.count || 0}</span>
                        <span className="text-xs text-slate-500 ml-1">reclamos</span>
                      </div>
                      <div className="space-y-1.5 text-[11px] text-slate-600">
                        <div className="flex justify-between">
                          <span>Promedio</span>
                          <span className="font-semibold text-slate-800">{col.tiempo_promedio_dias || 0}d</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Mayor tiempo</span>
                          <span className="font-semibold text-slate-800">{col.max_tiempo_dias || 0}d</span>
                        </div>
                        {col.max_folio && col.max_folio !== '-' && (
                          <div
                            className="text-[10px] text-blue-600 hover:text-blue-800 hover:underline truncate text-right cursor-pointer font-medium"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (col.max_reclamo_id) navigate(`/admin/reclamos?detail=${col.max_reclamo_id}`);
                            }}
                            data-testid={`pipeline-folio-${key}`}
                          >
                            {col.max_folio}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="px-3 py-1.5 text-center text-xs font-bold border-t" style={{ background: bg, borderColor: border, color: '#334155' }}>
                      {col.porcentaje || 0}%
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">Sin datos de pipeline</p>
          )}
        </CardContent>
      </Card>

      {/* Row 1: Area Chart (full width) */}
      <Card className="border-slate-200" data-testid="chart-reclamos-mes">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base">Reclamos por Mes</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <ChartSkeleton /> : areaData.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={areaData} onClick={handleAreaClick} style={{ cursor: 'pointer' }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <RechartsTooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="cantidad" name="Reclamos" stroke="#3b82f6" fill="#3b82f680" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Row 2: Two Donuts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200" data-testid="chart-estado-reclamo">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base">Por Estado de Reclamo</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <ChartSkeleton height={240} /> : estadoDonutData.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={estadoDonutData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value" nameKey="name" onClick={(d) => navToReclamos({ estado: estadoKeyMap[d.key] })} style={{ cursor: 'pointer' }}>
                    {estadoDonutData.map((entry) => (
                      <Cell key={entry.key} fill={ESTADO_COLORS[entry.key]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend wrapperStyle={{ fontSize: '13px' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="chart-estado-resolucion">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base">Por Estado de Resolucion</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <ChartSkeleton height={240} /> : resolucionDonutData.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={resolucionDonutData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value" nameKey="name" onClick={(d) => navToReclamos({ estado_resolucion: d.key })} style={{ cursor: 'pointer' }}>
                    {resolucionDonutData.map((entry) => (
                      <Cell key={entry.key} fill={RESOLUCION_COLORS[entry.key]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend wrapperStyle={{ fontSize: '13px' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Bar charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Por Proyecto (horizontal) */}
        <Card className="border-slate-200" data-testid="chart-por-proyecto">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base">Top Proyectos con Reclamos</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <ChartSkeleton height={240} /> : proyectoData.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={proyectoData} layout="vertical" onClick={(d) => {
                  if (d?.activePayload?.[0]?.payload?.id) navToReclamos({ proyecto_id: d.activePayload[0].payload.id });
                }} style={{ cursor: 'pointer' }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis dataKey="nombre" type="category" width={120} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <RechartsTooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
                        <p className="font-medium text-slate-700">{payload[0].payload.fullName}</p>
                        <p className="font-semibold text-blue-600">{payload[0].value} reclamos</p>
                      </div>
                    );
                  }} />
                  <Bar dataKey="cantidad" name="Reclamos" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Por Tipo */}
        <Card className="border-slate-200" data-testid="chart-por-tipo">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base">Reclamo vs Queja</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <ChartSkeleton height={240} /> : tipoData.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={tipoData} onClick={(d) => {
                  if (d?.activePayload?.[0]?.payload?.key) navToReclamos({ tipo_reclamo: d.activePayload[0].payload.key });
                }} style={{ cursor: 'pointer' }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="Cantidad" radius={[4, 4, 0, 0]}>
                    {tipoData.map((entry) => (
                      <Cell key={entry.key} fill={TIPO_COLORS[entry.key] || '#3b82f6'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 4: Resoluciones por mes */}
      <Card className="border-slate-200" data-testid="chart-resoluciones-mes">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base">Resoluciones por Mes</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <ChartSkeleton /> : resolucionesMesData.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={resolucionesMesData} onClick={(d) => {
                if (d?.activePayload?.[0]?.payload?.mes) {
                  const mes = d.activePayload[0].payload.mes;
                  const [y, m] = mes.split('-');
                  const lastDay = new Date(Number(y), Number(m), 0).getDate();
                  navToReclamos({ fecha_desde: `${mes}-01`, fecha_hasta: `${mes}-${lastDay}` });
                }
              }} style={{ cursor: 'pointer' }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <RechartsTooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: '13px' }} />
                <Bar dataKey="aprobadas" name="Aprobadas" fill={RESOLUCION_MES_COLORS.aprobadas} radius={[4, 4, 0, 0]} />
                <Bar dataKey="rechazadas" name="En Proceso" fill={RESOLUCION_MES_COLORS.rechazadas} radius={[4, 4, 0, 0]} />
                <Bar dataKey="pendientes" name="Pendientes" fill={RESOLUCION_MES_COLORS.pendientes} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const KPICard = ({ icon, label, value, color, bgIcon, borderColor, loading, onClick, testId }) => (
  <Card
    className={`border-l-4 ${borderColor} ${onClick ? 'cursor-pointer hover:shadow-lg' : ''} transition-shadow`}
    onClick={onClick}
    data-testid={testId}
  >
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className={`font-heading text-2xl font-bold ${color} mt-1`}>
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : value}
          </p>
        </div>
        <div className={`w-10 h-10 ${bgIcon} rounded-lg flex items-center justify-center`}>{icon}</div>
      </div>
    </CardContent>
  </Card>
);

const ChartSkeleton = ({ height = 280 }) => (
  <div className="flex items-center justify-center" style={{ height }}>
    <Loader2 className="w-8 h-8 animate-spin text-slate-300" />
  </div>
);

const EmptyChart = () => (
  <div className="flex items-center justify-center h-[200px] text-slate-400 text-sm">
    Sin datos para mostrar
  </div>
);

export default DashboardPage;
